import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { GoogleLogin, type CredentialResponse } from "@react-oauth/google";
import axios from "axios";

interface LoginData {
  key: string;
  email: string;
}

// Password login is shown ONLY when the frontend points at a local backend.
const IS_LOCAL = /localhost|127\.0\.0\.1/.test(process.env.REACT_APP_API_URL || "");

export default function SignIn() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("care@local");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  const onLoginSuccess = (data: LoginData) => {
    localStorage.setItem("token", data.key);
    localStorage.setItem("loginEmail", data.email);
    // Land on the Worklist — the single pane. (Home is retired.)
    navigate("/worklist", { replace: true });
    window.location.reload();
  };

  const errorInLogin = () => {};

  const responseGoogle = (res: CredentialResponse) => {
    axios
      .post(
        `${process.env.REACT_APP_API_URL}users/google-login-internal/`,
        { credential: res.credential },
        { headers: { "Content-Type": "application/json" } }
      )
      .then((response) => onLoginSuccess(response.data))
      .catch(() => {});
  };

  const passwordLogin = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");
    axios
      .post(
        `${process.env.REACT_APP_API_URL}users/dev-login/`,
        { email, password },
        { headers: { "Content-Type": "application/json" } }
      )
      .then((response) => onLoginSuccess(response.data))
      .catch((err) => setError(err?.response?.data?.error || "Login failed"));
  };

  return (
    <div className="mt-16 mb-16 flex h-full w-full items-center justify-center px-2 md:mx-0 md:px-0 lg:mb-10 lg:items-center lg:justify-start">
      <div className="mt-[10vh] w-full max-w-full flex-col items-center md:pl-4 lg:pl-0 xl:max-w-[420px]">
        <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.22em] text-clinic-400 dark:text-gray-500">
          Babynama Clinic-OS
        </p>
        <h4 className="mt-1 mb-4 text-3xl font-bold tracking-tight text-clinic-900 dark:text-white">
          Sign In
        </h4>
        <GoogleLogin onSuccess={responseGoogle} onError={errorInLogin} />

        {IS_LOCAL && (
          <form onSubmit={passwordLogin} className="mt-6 flex w-full flex-col gap-3">
            <div className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-clinic-500">
              Local dev login
            </div>
            {/* `outline-none` with no replacement made these invisible to keyboard users */}
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
              aria-label="Email"
              autoComplete="username"
              className="rounded-md border border-clinic-300 px-4 py-3 text-sm text-clinic-900 focus-visible:border-action-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-action-500 focus-visible:ring-offset-2"
            />
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              aria-label="Password"
              autoComplete="current-password"
              className="rounded-md border border-clinic-300 px-4 py-3 text-sm text-clinic-900 focus-visible:border-action-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-action-500 focus-visible:ring-offset-2"
            />
            {error && (
              <div role="alert" className="text-sm text-crit-600">
                {error} — check the email and password, then try again.
              </div>
            )}
            <button
              type="submit"
              className="touch-manipulation rounded-md bg-clinic-900 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-clinic-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-action-500 focus-visible:ring-offset-2 motion-reduce:transition-none"
            >
              Sign in with password
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
